<script setup lang="ts">
/**
 * ViewActionsMenu
 *
 * Context menu for view management actions.
 * Shows RBAC-aware options based on view ownership and type.
 */

import { computed } from 'vue'
import type { SavedView } from '../api/types'

interface Props {
    view: SavedView
    currentUserId: string
    isAdmin: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
    rename: [view: SavedView]
    duplicate: [view: SavedView]
    share: [view: SavedView]
    freeze: [view: SavedView]
    unfreeze: [view: SavedView]
    delete: [view: SavedView]
    setDefault: [view: SavedView]
    resetDefault: [view: SavedView]
    close: []
}>()

const isOwner = computed(() => props.view.createdBy?.id === props.currentUserId)
const isSystem = computed(() => props.view.visibility === 'system')
const isDynamic = computed(() => props.view.type === 'dynamic')
const isStatic = computed(() => props.view.type === 'static')

const canRename = computed(() => isOwner.value && !isSystem.value)
const canShare = computed(() => (isOwner.value || props.isAdmin) && !isSystem.value)
const canFreeze = computed(() => isDynamic.value && !isSystem.value && (isOwner.value || props.isAdmin))
const canUnfreeze = computed(() => isStatic.value && !isSystem.value && (isOwner.value || props.isAdmin))
const canDelete = computed(() => !isSystem.value && (isOwner.value || props.isAdmin))
</script>

<template>
    <div class="actions-menu" @click.stop>
        <!-- Set/Reset Default -->
        <button
            v-if="!view.isDefault"
            class="actions-menu__item"
            @click="emit('setDefault', view); emit('close')"
        >
            <span class="actions-menu__icon">⭐</span>
            Set as Default View
        </button>
        <button
            v-else
            class="actions-menu__item"
            @click="emit('resetDefault', view); emit('close')"
        >
            <span class="actions-menu__icon">☆</span>
            Remove Default Preference
        </button>

        <div class="actions-menu__separator" />

        <button
            v-if="canRename"
            class="actions-menu__item"
            @click="emit('rename', view); emit('close')"
        >
            <span class="actions-menu__icon">✏️</span>
            Rename
        </button>

        <button
            class="actions-menu__item"
            @click="emit('duplicate', view); emit('close')"
        >
            <span class="actions-menu__icon">📋</span>
            Duplicate
        </button>

        <button
            v-if="canShare"
            class="actions-menu__item"
            @click="emit('share', view); emit('close')"
        >
            <span class="actions-menu__icon">{{ view.visibility === 'shared' ? '🔒' : '👥' }}</span>
            {{ view.visibility === 'shared' ? 'Make Private' : 'Share with Org' }}
        </button>

        <div v-if="canFreeze || canUnfreeze" class="actions-menu__separator" />

        <button
            v-if="canFreeze"
            class="actions-menu__item"
            @click="emit('freeze', view); emit('close')"
        >
            <span class="actions-menu__icon">❄️</span>
            Freeze (Static Snapshot)
        </button>

        <button
            v-if="canUnfreeze"
            class="actions-menu__item"
            @click="emit('unfreeze', view); emit('close')"
        >
            <span class="actions-menu__icon">🔄</span>
            Unfreeze (Live Query)
        </button>

        <div v-if="canDelete" class="actions-menu__separator" />

        <button
            v-if="canDelete"
            class="actions-menu__item actions-menu__item--danger"
            @click="emit('delete', view); emit('close')"
        >
            <span class="actions-menu__icon">🗑️</span>
            Delete
        </button>
    </div>
</template>

<style scoped>
.actions-menu {
    width: 220px;
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    padding: 0.25rem 0;
    overflow: hidden;
}

.actions-menu__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
}

.actions-menu__item:hover {
    background: var(--bg-hover);
}

.actions-menu__item--danger {
    color: var(--color-danger);
}

.actions-menu__item--danger:hover {
    background: rgba(239, 68, 68, 0.08);
}

.actions-menu__icon {
    font-size: 0.875rem;
    width: 20px;
    text-align: center;
}

.actions-menu__separator {
    height: 1px;
    background: var(--border-color);
    margin: 0.25rem 0;
}
</style>
